import { useState, useCallback } from 'react';
import convertError from '../utils/error-converter.js';
import UserFriendlyError from '../utils/UserFriendlyError.js';
import callApi from '../utils/call-api.js';
import analyzeApiResponse from '../utils/analyze-api-response.js';

/** Helper: build a query string with proper Date -> ISO handling */
function toQueryString(obj = {}) {
    return Object.keys(obj)
        .map((key) => {
            let value = obj[key];

            // Normalize Dates to ISO UTC
            if (value instanceof Date) value = value.toISOString();

            if (Array.isArray(value)) {
                return value
                    .map((item) => {
                        const v =
                            item instanceof Date ? item.toISOString() : item;
                        return `${encodeURIComponent(key)}=${encodeURIComponent(
                            v
                        )}`;
                    })
                    .join('&');
            }

            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');
}

/**
 * Custom React hook for managing Reporting-related data.
 *
 * @returns {Object} An object containing state variables and functions related to user data.
 * @property {Array<Object>} tables - The array of returned table objects (empty if none)
 * @property {boolean} loading - A boolean indicating whether data is currently being loaded.
 * @property {Object|null} error - An error object containing details about any encountered error.
 * @property {Function} getWeeklyStats - A function to fetch weekly stats.
 */
const useReporting = () => {
    const [tables, setTables] = useState([]);
    const [table, setTable] = useState(null);
    const [charts, setCharts] = useState([]);
    const [queryType, setQueryType] = useState('');
    const [tickets, setTickets] = useState(null);
    const [auditPeriod, setAuditPeriod] = useState(null);
    const [auditContext, setAuditContext] = useState(null);
    const [publicScore, setPublicScore] = useState(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Gets weekly stats for a specified IT segment.
     *
     * @async
     * @function
     * @param {string} [itSegment=''] - The It segment to filter by.
     * @returns {Promise<Array>} A promise that resolves with a list of returned tables.
     */
    const getWeeklyStats = async (itSegment) => {
        try {
            setLoading(true);
            setError(null);

            const path = `/reporting/dashboard/weekly-stats?itSegment=${itSegment}`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const data = await response.json();
            // console.log('✅ Weekly Stats Response:', data);
            setTables(data);
            return data;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetches weekly Copilot ticket reports.
     *
     * @async
     * @function
     * @returns {Promise<Array<Object>>} Weekly Copilot reports
     */
    const getCopilotWeeklyReports = async () => {
        try {
            setLoading(true);
            setError(null);

            const path = `/reporting/copilot/weekly`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const { data } = await response.json();
            return data;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Gets queried stats with specified query parameters.
     *
     * @async
     * @function
     * @param {string} queryOptions
     * @returns {Promise<Object>} A promise that resolves with an object of the returned tables and charts.
     */
    const runQuery = async (queryOptions) => {
        try {
            setLoading(true);
            setError(null);
            setTable(null);
            setTables([]);
            setCharts([]);
            setQueryType('');

            const queryString = toQueryString(queryOptions);

            const path = `/reporting/query?${queryString}`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }
            // const data = await response.json();
            // console.log('✅ Run Query Response:', data);
            const {
                table: returnedTable,
                tables: returnedTables,
                charts: returnedCharts,
                queryType: returnedQueryType
            } = await response.json();
            setTable(returnedTable);
            setTables(returnedTables);
            setCharts(returnedCharts);
            setQueryType(returnedQueryType);
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Resets all stats.
     *
     * @async
     * @function
     * @returns {Promise<void>} A promise that resolves when finished.
     */
    const reset = async () => {
        try {
            setError(null);
            setTable(null);
            setTables([]);
            setCharts([]);
            setQueryType('');
            setTickets(null);
            setAuditPeriod(null);
            // console.log('✅ Reset called');
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetches ticket tag options from the API.
     *
     * This asynchronous function fetches a list of ticket tags from the `/reporting/tickets/ticket-tags` endpoint.
     * It handles loading state, errors, and processes the response.
     *
     * @async
     * @function getTicketTagOptions
     * @returns {Promise<Array>} Resolves with the ticket tag options.
     */
    const getTicketTagOptions = async () => {
        try {
            setLoading(true);
            setError(null);

            const path = `/reporting/tickets/ticket-tags`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const data = await response.json();
            // console.log('✅ Ticket Tag Options:', data);
            return data;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetches ticket type options from the API.
     *
     * This asynchronous function fetches a list of ticket types from the `/reporting/tickets/ticket-types` endpoint.
     * It handles loading state, errors, and processes the response.
     *
     * @async
     * @function getTicketTypeOptions
     * @returns {Promise<Array>} Resolves with the ticket type options.
     */
    const getTicketTypeOptions = async () => {
        try {
            setLoading(true);
            setError(null);

            const path = `/reporting/tickets/ticket-types`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const data = await response.json();
            // console.log('✅ Ticket Type Options:', data);
            return data;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Gets random tickets with specified query parameters.
     *
     * @async
     * @function
     * @param {string} queryOptions
     * @returns {Promise<Array<Object>>} A promise that resolves with an array of returned ticket objects.
     */
    const getRandomTickets = async (queryOptions) => {
        try {
            setLoading(true);
            setError(null);
            setTickets([]);
            setAuditPeriod(null);

            const queryString = toQueryString(queryOptions);

            const path = `/reporting/tickets/random?${queryString}`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const returnedTickets = await response.json();
            // console.log('✅ Random Tickets Response:', returnedTickets);
            setTickets(returnedTickets);
            return returnedTickets;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Prioritized weekly audit tickets (survey / SLA heuristics) for one technician.
     */
    const getWeeklyAuditTickets = async (queryOptions) => {
        try {
            setLoading(true);
            setError(null);
            setTickets([]);
            setAuditPeriod(null);
            setAuditContext(null);
            setPublicScore(null);

            const cleaned = { ...queryOptions };
            Object.keys(cleaned).forEach((k) => {
                if (
                    cleaned[k] === '' ||
                    cleaned[k] === null ||
                    cleaned[k] === undefined
                ) {
                    delete cleaned[k];
                }
            });
            if (cleaned.completedAuditsOnly === false) {
                delete cleaned.completedAuditsOnly;
            }

            const queryString = toQueryString(cleaned);
            const path = `/reporting/tickets/weekly-audit?${queryString}`;
            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const data = await response.json();
            setTickets(data.tickets || []);
            setAuditPeriod(data.period || null);
            setAuditContext(data.auditContext || null);
            setPublicScore(data.publicScore || null);
            return data;
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Weekly audit team names from Oracle (`/reporting/weekly-audit/teams`) — Pearl DB
     * (`teams` collection + distinct `users.hdTeam`); no Oyster required for dev.
     */
    const getWeeklyAuditTeams = useCallback(async () => {
        try {
            setError(null);
            const response = await callApi(
                '/reporting/weekly-audit/teams',
                'get'
            );
            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }
            return response.json();
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        }
    }, []);

    /**
     * Oracle technicians on a Pearl team (for tech dropdown).
     * Stable reference so consumers' useEffects do not re-fetch in a loop.
     */
    const getWeeklyAuditTechsForTeam = useCallback(async (teamHdName) => {
        try {
            setError(null);
            const q = teamHdName
                ? `?teamHdName=${encodeURIComponent(teamHdName)}`
                : '';
            const response = await callApi(
                `/reporting/weekly-audit/techs-for-team${q}`,
                'get'
            );
            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }
            return response.json();
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        }
    }, []);

    /**
     * Management reporting: monthly sign-off rollup per technician.
     *
     * @param {string} monthYm `YYYY-MM`
     */
    const getWeeklyAuditManagementSummary = async (monthYm) => {
        try {
            setError(null);
            const path = `/reporting/weekly-audit/management-summary?month=${encodeURIComponent(
                monthYm
            )}`;
            const response = await callApi(path, 'get');
            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }
            return response.json();
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
            return null;
        }
    };

    /**
     * Gets queried stats with specified query parameters.
     *
     * @async
     * @function
     * @param {string} queryOptions
     * @returns {Promise<Object>} A promise that resolves with an object of the returned tables and charts.
     */
    const getLicenseReportingData = async (queryOptions) => {
        try {
            setLoading(true);
            setError(null);
            setTable(null);
            setCharts([]);

            const queryString = toQueryString(queryOptions);

            const path = `/reporting/licenses?${queryString}`;

            const response = await callApi(path, 'get');

            if (!response.ok) {
                const errorMessage = await analyzeApiResponse(response);
                throw new UserFriendlyError(errorMessage);
            }

            const { table: returnedTable, charts: returnedCharts } =
                await response.json();
            // console.log('✅ License Reporting Response:', {
            //     returnedTable,
            //     returnedCharts
            // });
            setTable(returnedTable);
            setCharts(returnedCharts);
        } catch (err) {
            const convertedUserFriendlyError = convertError(err);
            setError(convertedUserFriendlyError);
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        error,
        tables,
        getWeeklyStats,
        table,
        charts,
        runQuery,
        queryType,
        reset,
        getTicketTagOptions,
        getTicketTypeOptions,
        tickets,
        getRandomTickets,
        getWeeklyAuditTickets,
        auditPeriod,
        auditContext,
        publicScore,
        getWeeklyAuditTeams,
        getWeeklyAuditTechsForTeam,
        getWeeklyAuditManagementSummary,
        getLicenseReportingData,
        getCopilotWeeklyReports
    };
};

export default useReporting;
